/**
 * Patient Profile Form Component (Step 2)
 *
 * Profile form for patient registration workflow.
 * Includes address, phone, date of birth, and optional photo upload.
 * Based on the existing DieticianProfileForm pattern.
 *
 * @module components/patient-registration/PatientProfileForm
 */

import React from 'react';
import { Button } from '@/components/ui/Button';
import { FormField } from '@/components/ui/FormField';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { CountryPhoneSelector } from '@/components/ui/CountryPhoneSelector';
import { ProfilePhotoUpload } from '@/components/ui/ProfilePhotoUpload';
import { useTheme } from '@/hooks/useTheme';
import { usePatientProfileFormState } from '@/hooks/usePatientProfileFormState';
import { cn } from '@/utils/cn';
import type { PatientProfileFormData } from '@/types/patientRegistration';
import { AUSTRALIAN_STATE_OPTIONS, COUNTRY_OPTIONS } from '@/constants/geographic';

/**
 * Component props interface
 */
export interface PatientProfileFormProps {
  onSubmit: (data: PatientProfileFormData) => Promise<void>;
  initialData?: Partial<PatientProfileFormData>;
  isLoading: boolean;
  errors: Record<string, string>;
  mode?: 'registration' | 'edit';
  onCancel?: () => void;
}

/**
 * PatientProfileForm Component
 *
 * Step 2 of the patient registration workflow.
 * Collects personal information including address, contact details, and birth date.
 */
export const PatientProfileForm: React.FC<PatientProfileFormProps> = ({
  onSubmit,
  initialData,
  isLoading,
  errors,
  mode = 'registration',
  onCancel,
}) => {
  const { isDark } = useTheme();

  // Use the custom hook for form state and logic
  const {
    formData,
    fieldErrors,
    fieldRefs,
    handleFieldChange,
    handleFieldBlur,
    handlePhoneChange,
    handlePhotoChange,
    handleSubmit,
  } = usePatientProfileFormState({
    initialData,
    onSubmit,
  });

  // Get today's date for max date validation
  const today = new Date().toISOString().split('T')[0];

  return (
    <form onSubmit={handleSubmit} className="space-y-6" role="form">
      {/* Form error display */}
      {errors.form && (
        <div className={cn(
          'p-4 rounded-lg border',
          isDark
            ? 'bg-red-900/20 border-red-400 text-red-300'
            : 'bg-red-50 border-red-200 text-red-800'
        )} data-testid="form-error">
          <p className="text-sm font-medium">{errors.form}</p>
        </div>
      )}

      {/* Personal Information Section */}
      <div className="space-y-4">
        <h3 className={cn(
          'text-lg font-semibold',
          isDark ? 'text-white' : 'text-gray-900'
        )}>
          Personal Information
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="First Name"
            required
            error={fieldErrors.firstName}
          >
            <Input
              ref={(el) => { fieldRefs.current.firstName = el; }}
              type="text"
              value={formData.firstName}
              onChange={(e) => handleFieldChange('firstName', e.target.value)}
              onBlur={() => handleFieldBlur('firstName')}
              placeholder="Enter your first name"
              disabled={isLoading}
              autoComplete="given-name"
              aria-required="true"
            />
          </FormField>

          <FormField
            label="Last Name"
            required
            error={fieldErrors.lastName}
          >
            <Input
              ref={(el) => { fieldRefs.current.lastName = el; }}
              type="text"
              value={formData.lastName}
              onChange={(e) => handleFieldChange('lastName', e.target.value)}
              onBlur={() => handleFieldBlur('lastName')}
              placeholder="Enter your last name"
              disabled={isLoading}
              autoComplete="family-name"
              aria-required="true"
            />
          </FormField>
        </div>
      </div>

      {/* Address Section */}
      <div className="space-y-4">
        <h3 className={cn(
          'text-lg font-semibold',
          isDark ? 'text-white' : 'text-gray-900'
        )}>
          Address Information
        </h3>

        <FormField
          label="Street Address"
          required
          error={fieldErrors['address.street']}
        >
          <Input
            ref={(el) => { fieldRefs.current['address.street'] = el; }}
            type="text"
            value={formData.address.street}
            onChange={(e) => handleFieldChange('address.street', e.target.value)}
            onBlur={() => handleFieldBlur('address.street')}
            placeholder="Enter street address"
            disabled={isLoading}
            autoComplete="street-address"
            aria-required="true"
          />
        </FormField>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="City"
            required
            error={fieldErrors['address.city']}
          >
            <Input
              ref={(el) => { fieldRefs.current['address.city'] = el; }}
              type="text"
              value={formData.address.city}
              onChange={(e) => handleFieldChange('address.city', e.target.value)}
              onBlur={() => handleFieldBlur('address.city')}
              placeholder="Enter city name"
              disabled={isLoading}
              autoComplete="address-level2"
              aria-required="true"
            />
          </FormField>

          <FormField
            label="State/Territory"
            required
            error={fieldErrors['address.state']}
          >
            <Select
              ref={(el) => { fieldRefs.current['address.state'] = el; }}
              value={formData.address.state}
              onValueChange={(value) => handleFieldChange('address.state', value)}
              onBlur={() => handleFieldBlur('address.state')}
              disabled={isLoading}
              state={fieldErrors['address.state'] ? 'error' : 'default'}
              options={AUSTRALIAN_STATE_OPTIONS.map(option => ({
                value: option.value,
                label: option.label,
                disabled: false
              }))}
              required
            />
          </FormField>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="Postcode"
            required
            error={fieldErrors['address.postal_code']}
          >
            <Input
              ref={(el) => { fieldRefs.current['address.postal_code'] = el; }}
              type="text"
              value={formData.address.postal_code}
              onChange={(e) => handleFieldChange('address.postal_code', e.target.value)}
              onBlur={() => handleFieldBlur('address.postal_code')}
              placeholder="Enter postal code"
              maxLength={4}
              disabled={isLoading}
              autoComplete="postal-code"
              aria-required="true"
            />
          </FormField>

          <FormField
            label="Country"
            required
            error={fieldErrors['address.country']}
          >
            <Select
              value={formData.address.country}
              onValueChange={(value) => handleFieldChange('address.country', value)}
              disabled={true} // Country is fixed to Australia
              state="default"
              options={COUNTRY_OPTIONS.map(option => ({
                value: option.value,
                label: option.label,
                disabled: false
              }))}
              required
            />
          </FormField>
        </div>
      </div>

      {/* Contact Section */}
      <div className="space-y-4">
        <h3 className={cn(
          'text-lg font-semibold',
          isDark ? 'text-white' : 'text-gray-900'
        )}>
          Contact Information
        </h3>

        <FormField
          label="Mobile Phone"
          required
          error={fieldErrors.phone}
          helperText="Your mobile phone number for important notifications"
        >
          <CountryPhoneSelector
            value={{
              countryCode: `+${formData.phone.country_code}`,
              isoCode: formData.phone.iso_code,
              number: formData.phone.number
            }}
            onChange={handlePhoneChange}
            disabled={isLoading}
            error={fieldErrors.phone}
            required
          />
        </FormField>

        <FormField
          label="Date of Birth"
          required
          error={fieldErrors.date_of_birth}
        >
          <Input
            ref={(el) => { fieldRefs.current.date_of_birth = el; }}
            type="date"
            value={formData.date_of_birth}
            onChange={(e) => handleFieldChange('date_of_birth', e.target.value)}
            onBlur={() => handleFieldBlur('date_of_birth')}
            max={today}
            disabled={isLoading}
            autoComplete="bday"
            aria-required="true"
          />
        </FormField>
      </div>

      {/* Photo Section */}
      <div className="space-y-4">
        <h3 className={cn(
          'text-lg font-semibold',
          isDark ? 'text-white' : 'text-gray-900'
        )}>
          Profile Photo
        </h3>

        <FormField
          label="Profile Photo"
          error={fieldErrors.photo}
          helperText="Optional: Upload a photo for your profile (max 5MB, JPEG/PNG)"
        >
          <ProfilePhotoUpload
            value={formData.photo ? URL.createObjectURL(formData.photo) : undefined}
            onChange={(photoData) => {
              if (photoData) {
                // Convert base64 data URL to File object
                fetch(photoData)
                  .then(res => res.blob())
                  .then(blob => {
                    const file = new File([blob], 'profile-photo.jpg', { type: 'image/jpeg' });
                    handlePhotoChange(file);
                  })
                  .catch(() => handlePhotoChange(undefined));
              } else {
                handlePhotoChange(undefined);
              }
            }}
            disabled={isLoading}
            error={fieldErrors.photo}
          />
        </FormField>
      </div>

      {/* Submit Button */}
      <div className={`flex ${mode === 'edit' ? 'justify-between' : 'justify-end'} pt-6 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`} data-testid="button-container">
        {mode === 'edit' && onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={isLoading}
            className="min-w-[120px]"
          >
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          disabled={isLoading}
          className="min-w-[120px]"
        >
          {isLoading ? (
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Saving...</span>
            </div>
          ) : (
            mode === 'edit' ? 'Save Changes' : 'Complete Registration'
          )}
        </Button>
      </div>

      {/* Form Summary */}
      <div className={cn(
        'text-sm',
        isDark ? 'text-secondary-400' : 'text-secondary-600'
      )}>
        <p>
          By continuing, you confirm that the information provided is accurate and up-to-date.
          This information will be used to create your patient profile.
        </p>
      </div>
    </form>
  );
};

export default PatientProfileForm;